import { useEffect, useState } from 'react';
import {
    AdvancedMarker,
    InfoWindow,
    useAdvancedMarkerRef
} from '@vis.gl/react-google-maps';
import { LocksmithNearestList } from '../utils/types';
import { JobStatus } from '../utils/enum';
import clsx from 'clsx';
import { getInitials } from '../utils/helpers';
import { PhoneIcon, UserIcon, AtSymbolIcon, MapPinIcon, ClockIcon, XCircleIcon, DocumentTextIcon, WrenchScrewdriverIcon, ArrowPathRoundedSquareIcon } from '@heroicons/react/24/outline';
import { parsePhoneNumberWithError } from 'libphonenumber-js';
import moment from 'moment';
import { IconTitleButton } from './icon-title-button';
import { request } from '../utils/request';
import { api } from '../utils/api';
import AssignForm from './assign-job';

interface LocksmithMarkerProps {
    details: LocksmithNearestList;
    address?: string;
    address_coords?: google.maps.LatLngLiteral
}

interface DataFromAddressType {
    distanceInMeters: number
    timeInSeconds: number
}

export const LocksmithMarker = ({ details, address, address_coords }: LocksmithMarkerProps) => {
    const [infowindowOpen, setInfowindowOpen] = useState(false);
    const [markerRef, marker] = useAdvancedMarkerRef();
    const position: google.maps.LatLngLiteral = { lat: details.locksmith.current_location.coordinates[1], lng: details.locksmith.current_location.coordinates[0] };
    const [dataFromAddress, setDataFromAddress] = useState<DataFromAddressType | undefined>(undefined);
    const [dataFromAddressLoading, setDataFromAddressLoading] = useState(false);
    const job = details.locksmith.jobs[0];
    const getRingStatusColorClass = () => {
        if (job?.job_status === JobStatus.PENDING) return 'bg-yellow-600'
        if (job?.job_status !== JobStatus.ACCEPTED) return 'bg-green-500'
        return 'bg-gray-300'
    }
    const getBadgeStatusColorClass = () => {
        if (job?.job_status === JobStatus.PENDING) return 'bg-yellow-50 text-yellow-700 ring-yellow-600/20'
        if (job?.job_status === JobStatus.REJECTED) return 'bg-red-50 text-red-700 ring-red-600/20'
        if (job?.job_status !== JobStatus.ACCEPTED) return 'bg-green-50 text-green-700 ring-green-600/20'
        return 'bg-gray-50 text-gray-700 ring-gray-600/20'
    }
    const clockOut = async () => {
        await request('PUT', api.locksmith.clock + '/' + details.id, { clocked_in: 0, date: new Date().toISOString() })
    }
    const getDistance = async () => {
        if (!address_coords) return;
        const query = new URLSearchParams();
        query.append('locksmith_id', details.locksmith.id.toString());
        query.append('latitude', address_coords?.lat.toString());
        query.append('longitude', address_coords?.lng.toString());
        setDataFromAddressLoading(true)
        try {
            const response = await request('GET', api.locksmith.distanceNTime + '?' + query.toString());
            setDataFromAddress(response.data.data)
        } catch (error) {
            console.log("🚀 ~ getDistance ~ error:", error)
        }
        finally { setDataFromAddressLoading(false) }
    }

    useEffect(() => {
        if (!address_coords) setDataFromAddress(undefined);
    }, [address_coords])
    return (
        <>
            <AdvancedMarker
                ref={markerRef}
                onClick={() => setInfowindowOpen(prev => !prev)}
                position={position}
                title={'Locksmith Marker'}
            >
                <div className={`relative inline-block p-1 rounded-full`} style={{ backgroundColor: details.locksmith.locksmith_color }}>
                    <span className="inline-flex size-8 items-center justify-center rounded-full bg-gray-500">
                        <span className="text-lg font-medium text-white">{getInitials(details.full_name)}</span>
                    </span>
                    <span className={clsx("absolute bottom-0 z-10 right-1 block size-2 rounded-full ring-1 ring-white", getRingStatusColorClass())} />
                    <div
                        className="w-0 h-0 border-l-[8px] border-r-[8px] border-t-[6px] border-l-transparent border-r-transparent border-t-black absolute bottom-[-0.3rem] left-1/2 transform -translate-x-1/2"
                        style={{ borderTopColor: details.locksmith.locksmith_color }}
                    ></div>
                </div>
            </AdvancedMarker>
            {infowindowOpen && (
                <InfoWindow
                    anchor={marker}
                    headerDisabled
                    shouldFocus={false}
                    maxWidth={400}
                    disableAutoPan
                    onCloseClick={() => setInfowindowOpen(false)}>
                    <div className="lg:col-start-3 lg:row-end-1">
                        <div className="rounded-lg shadow-sm pb-1">
                            <h2 className='text-lg font-medium mb-2'>Tech Info</h2>
                            <dl className="flex flex-wrap">
                                <InfoIcon icon={UserIcon} label={details.full_name} />
                                <InfoIcon icon={PhoneIcon} label={parsePhoneNumberWithError(details.locksmith.contact_number).formatInternational()} />
                                <InfoIcon icon={AtSymbolIcon} label={details.email} />
                                {dataFromAddressLoading ? <div>
                                    <InfoIcon icon={MapPinIcon} label={`...`} />
                                    <InfoIcon icon={ClockIcon} label={`...`} />
                                </div>
                                    : (dataFromAddress && address_coords) && <>
                                        <InfoIcon icon={MapPinIcon} label={`${(dataFromAddress.distanceInMeters / 1600).toFixed(2)} miles`} />
                                        <InfoIcon icon={ClockIcon} label={`${Math.floor(moment.duration(dataFromAddress.timeInSeconds, 'seconds').asHours())} hrs ${moment.duration(dataFromAddress.timeInSeconds, 'seconds').minutes()} mins`} />
                                    </>}
                            </dl>
                        </div>
                        {(job && ![JobStatus.COMPLETED, JobStatus.REJECTED].includes(job?.job_status)) && <div className="rounded-lg shadow-sm pt-2">
                            <div className='flex justify-between items-center mb-2'>
                                <h2 className='text-lg font-medium'>Job Info</h2>
                                <div className="flex-none self-end">
                                    <dt className="sr-only">Status</dt>
                                    <dd className={clsx("inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset", getBadgeStatusColorClass())}>
                                        {job?.job_status?.toUpperCase?.()}
                                    </dd>
                                </div>
                            </div>
                            <dl className="flex flex-wrap">
                                <InfoIcon icon={UserIcon} label={job.customer_name} />
                                <InfoIcon icon={PhoneIcon} label={parsePhoneNumberWithError(job.customer_contact_number).formatInternational()} />
                                <InfoIcon icon={MapPinIcon} label={job.customer_address} />
                                <InfoIcon icon={WrenchScrewdriverIcon} label={job.job_issue} />
                                <InfoIcon icon={ClockIcon} label={'Created at:' + moment(job.created_at).format(' lll')} />
                                {job.reject_reason && <InfoIcon icon={XCircleIcon} label={'Reason: ' + job.reject_reason} />}
                                {job.job_notes && <InfoIcon icon={DocumentTextIcon} label={'Notes by tech: ' + job.job_notes} />}
                            </dl>
                        </div>}
                        <div className='mt-2 flex justify-start w-full'>
                            {address_coords && <IconTitleButton icon={ArrowPathRoundedSquareIcon} onClick={getDistance} title="Get Distance & Time" />}
                        </div>
                        <div className='mt-2 flex justify-between w-full'>
                            {[JobStatus.COMPLETED, JobStatus.REJECTED, undefined].includes(job?.job_status) && <AssignForm coords={address_coords} address={address} id={details.locksmith.id} />}
                            {[JobStatus.COMPLETED, JobStatus.REJECTED, JobStatus.PENDING, undefined].includes(job?.job_status) && <IconTitleButton onClick={clockOut} icon={ClockIcon} title="Clock Out" />}
                        </div>
                    </div>
                </InfoWindow>
            )}
        </>
    );
};

interface InfoIconProps {
    icon: React.ElementType
    label: string
}
const InfoIcon = (props: InfoIconProps) => {
    return <div className="flex w-full flex-none gap-x-4">
        <dt className="flex-none">
            <props.icon aria-hidden="true" className="h-6 w-5 text-gray-400" />
        </dt>
        <dd className="text-sm/6 font-medium text-gray-900">{props.label}</dd>
    </div>
}